import { JSX } from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import FileUploadRequiredIcon from './FileUploadRequiredIcon';

interface Props {
  options: Array<{
    id: string | number;
    label: string;
    requires_file_upload?: boolean;
  }>;
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
}

export default function MultiselectInput({
  options,
  value,
  onChange,
}: Props): JSX.Element {
  const handleToggle = (id: string | number): void => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        paddingBottom: 2.5,
      }}
    >
      {options.map((option) => {
        const isSelected = value.includes(option.id);
        return (
          <Box
            key={option.id}
            onClick={() => handleToggle(option.id)}
            data-testid={`multiselect-option-${option.id}`}
            sx={{
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderRadius: '10px',
              border: isSelected ? '2px solid' : '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              bgcolor: 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                borderColor: isSelected ? 'primary.main' : 'grey.400',
              },
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '4px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(isSelected
                  ? { bgcolor: 'primary.main' }
                  : { border: '2px solid', borderColor: 'grey.300' }),
              }}
            >
              {isSelected && (
                <CheckIcon
                  sx={{ fontSize: 14, color: 'primary.contrastText' }}
                />
              )}
            </Box>
            <Typography
              fontWeight={isSelected ? 600 : 'normal'}
              sx={{ flex: 1 }}
            >
              {option.label}
            </Typography>
            {option.requires_file_upload && <FileUploadRequiredIcon />}
          </Box>
        );
      })}
    </Box>
  );
}
